import { createTheme } from '@mui/material';
import type {
  Theme as AugmentedTheme,
  ThemeOptions as AugmentedThemeOptions,
} from '@mui/material/styles';
import type { TypographyStyleOptions } from '@mui/material/styles/createTypography';
import type { FontFace, TextTransformProperty } from 'csstype';
import type { CSSObject } from 'tss-react';

import { ViewportSize } from './viewportSize';

/**
 * @deprecated moving to new style theme
 */
export type SunrunTypeVariant =
  | 'display-m'
  | 'heading-xxs'
  | 'body-s'
  | 'body-xs';

export type ColorNames =
  | 'white'
  | 'black'
  | 'gray'
  | 'lightGray'
  | 'mediumGrey'
  | 'darkGray'
  | 'grey'
  | 'offWhite'
  | 'heroYellow'
  | 'heroYellow10'
  | 'chargeYellow'
  | 'chargeRed'
  | 'chargeBlue'
  | 'mediumBlue'
  | 'heroBlue'
  | 'primaryMain'
  | 'primaryLight'
  | 'primaryDark'
  | 'secondaryMain'
  | 'secondaryLight'
  | 'secondaryDark'
  | 'errorMain'
  | 'errorMainDark'
  | 'errorLight'
  | 'errorDark'
  | 'successMain'
  | 'successLight'
  | 'successDark'
  | 'warningMain'
  | 'warningLight'
  | 'warningDark'
  | 'infoMain'
  | 'infoLight'
  | 'infoDark'
  | 'tintsChargeBlue20'
  | 'tintsChargeBlue30'
  | 'tintsChargeBlue50'
  | 'tintsChargeBlue60'
  | 'tintsChargeBlue70'
  | 'tintsChargeBlue80'
  | 'tintsChargeRed60'
  | 'tintsHeroBlue10'
  | 'tintsHeroBlue20'
  | 'tintsHeroBlue40'
  | 'tintsHeroBlue60'
  | 'tintsHeroBlue70'
  | 'tintsHeroBlue80'
  | 'tintsNeutral10'
  | 'tintsNeutral20'
  | 'tintsNeutral30'
  | 'tintsOffWhite40'
  | 'tintsOffWhite50'
  | 'tintsOffWhite60'
  | 'tintsOffWhite80'
  | 'tintsOffWhite90'
  | 'textPrimary'
  | 'semanticColorsError50'
  | 'semanticColorsError60'
  | 'semanticColorsSuccess20'
  | 'semanticColorsSuccess90'
  | 'interactiveHeroBlueSubtle'
  | 'foregroundInverseSubtle'
  | 'tintsMediumBlue20'
  | 'tintsMediumBlue60'
  | 'tintsMediumBlue80'
  | 'tintsHeroBlue90';

export type TypeVariant =
  | '88_medium'
  | '72_medium'
  | '64_medium'
  | '56_medium'
  | '44_regular'
  | '44_medium'
  | '40_regular'
  | '40_medium'
  | '32_regular'
  | '32_medium'
  | '28_medium'
  | '28_medium-hyperlink'
  | '24_regular'
  | '24_medium'
  | '24_medium-hyperlink'
  | '20_regular'
  | '20_medium'
  | '20_medium-hyperlink'
  | '18_medium'
  | '18_regular'
  | '18_medium-hyperlink'
  | '16_regular'
  | '16_medium'
  | '16_medium-hyperlink'
  | '14_regular'
  | '14_medium'
  | '14_medium-hyperlink'
  | '12_semibold'
  | '12_regular'
  | '12_medium-hyperlink'
  | '12_chip'
  | '10_semibold';

export type ZLevel =
  | 'dropdown'
  | 'sticky'
  | 'fixed'
  | 'tooltip'
  | 'modalBackdrop'
  | 'modal'
  | 'popover';

export type FontWeight = 'semibold' | 'medium' | 'regular';

export const aspectRatio = {
  '1/1': '1 / 1',
  '5/2': '5 / 2',
  '5/4': '5 / 4',
  '4/5': '4 / 5',
  '8/5': '8 / 5',
  '4/3': '4 / 3',
  '8/3': '8 / 3',
  '3/4': '3 / 4',
  '3/2': '3 / 2',
  '3/1': '3 / 1',
  '2/3': '2 / 3',
  '16/10': '16 / 10',
  '16/5': '16 / 5',
  '10/16': '10 / 16',
  '16/9': '16 / 9',
  '32/9': '32 / 9',
  '9/16': '9 / 16',
  '9/8': '9 / 8',
  '2/1': '2 / 1',
  '4/1': '4 / 1',
  '1/2': '1 / 2',
  '21/9': '21 / 9',
  '14/3': '14 / 3',
  '9/21': '9 / 21',
  '6/7': '6 / 7',
} as const;

type AspectRatio = typeof aspectRatio;

declare module '@mui/material/styles/createTheme' {
  // allow configuration using `createTheme`
  interface Theme {
    brandColors: Record<ColorNames, string>;
    /**
     * @deprecated moving to new style theme
     */
    brandType: Record<SunrunTypeVariant, CSSObject>;
    type: Record<TypeVariant, CSSObject>;
    fontWeights: Record<FontWeight, number>;
    zLevels: Record<ZLevel, number>;
    headerHeight: {
      height: string;
    };
    aspectRatio: AspectRatio;
  }

  interface ThemeOptions {
    brandColors: Record<ColorNames, string>;
    brandType: Record<SunrunTypeVariant, TypographyStyleOptions>;
    type: Record<TypeVariant, TypographyStyleOptions>;
    fontWeights: Record<FontWeight, number>;
    zLevels: Record<ZLevel, number>;
    headerHeight: {
      height: string;
    };
    aspectRatio: AspectRatio;
  }
}
export type ThemeType = AugmentedTheme;

const roobertFont: FontFace = {
  fontFamily: 'Roobert',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 400,
  src: `url(../public/cmsAssets/fonts/Roobert/Roobert-Light.woff2) format('woff2')`,
};

export const brandColors = {
  white: '#FFFFFF',
  black: '#000000',
  gray: '#D2D2D2',
  lightGray: '#CCCCCC',
  mediumGrey: '#adadad',
  grey: '#B3B0A8',
  darkGray: '#9e9e9e',
  offWhite: '#FFFBF0',
  heroYellow: '#FFF59B',
  heroYellow10: '#48452C',
  chargeYellow: '#FCE838',
  chargeRed: '#EE8C66',
  chargeBlue: '#A5C9FF',
  mediumBlue: '#49517D',
  heroBlue: '#181E3A',
  primaryMain: '#1F2647',
  primaryLight: '#EBD1D0',
  primaryDark: '#011F3A',
  secondaryMain: '#FFF6A1',
  secondaryLight: '#FFFDEA',
  secondaryDark: '#A39D63',
  errorMain: '#9E1E19',
  errorMainDark: '#FF8383',
  errorLight: '#EBD1D0',
  errorDark: '#661411',
  successMain: '#007505',
  successLight: '#CAE2CB',
  successDark: '#014B04',
  warningMain: '#E5C100',
  warningLight: '#FAF2C9',
  warningDark: '#957E01',
  infoMain: '#0065C1',
  infoLight: '#D2E4F4',
  infoDark: '#01407A',
  tintsChargeBlue20: '#4C5C74',
  tintsChargeBlue30: '#6A81A4',
  tintsChargeBlue50: '#A5C9FF',
  tintsChargeBlue60: '#C3DBFF',
  tintsChargeBlue70: '#DBE9FF',
  tintsChargeBlue80: '#EEF5FF',
  tintsChargeRed60: '#F3B197',
  tintsHeroBlue10: '#070911',
  tintsHeroBlue20: '#0C0E1B',
  tintsHeroBlue40: '#141930',
  tintsHeroBlue60: '#616578',
  tintsHeroBlue70: '#9FA1AD',
  tintsHeroBlue80: '#D4D5DA',
  tintsHeroBlue90: '#EFF0F2',
  tintsMediumBlue20: '#22263A',
  tintsMediumBlue60: '#878DAA',
  tintsMediumBlue80: '#DBDCE5',
  tintsMediumBlue90: '#F2F3F6',
  tintsNeutral10: '#474643',
  tintsNeutral20: '#6F6D68',
  tintsNeutral30: '#A3A09A',
  tintsOffWhite40: '#D0CCC3',
  tintsOffWhite50: '#F4F1EC',
  tintsOffWhite60: '#FFFCF5',
  tintsOffWhite80: '#FFFEFC',
  tintsOffWhite90: '#FFFFFE',
  textPrimary: '#171E39',
  semanticColorsError50: '#9E1E19',
  semanticColorsError60: '#BC6460',
  semanticColorsSuccess20: '#013803',
  semanticColorsSuccess90: '#EDF5ED',
  interactiveHeroBlueSubtle: '#1f26471a',
  foregroundInverseSubtle: '#ffffff1a',
};

const fontWeights = {
  semibold: 600,
  medium: 500,
  regular: 400,
};

// For one-off sizes that are not in @sunrun/mui-theme FontSize
function pxToRem(px: number): string {
  return `${px / 16}rem`;
}

const type = {
  '88_medium': {
    fontSize: pxToRem(88),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(96.8), // 110%
    letterSpacing: pxToRem(-1.76),
  },
  '72_medium': {
    fontSize: pxToRem(72),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(72), // 100%
    letterSpacing: pxToRem(-1.44),
  },
  '64_medium': {
    fontSize: pxToRem(64),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(70.4), // 110%
    letterSpacing: pxToRem(-1.28),
  },
  '56_medium': {
    fontSize: pxToRem(56),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(61.6), // 110%
    letterSpacing: pxToRem(-1.12),
  },
  '44_regular': {
    fontSize: pxToRem(44),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(52.8), // 120%
    letterSpacing: pxToRem(-0.88),
  },
  '44_medium': {
    fontSize: pxToRem(44),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(48.4), // 110%
    letterSpacing: pxToRem(-0.88),
  },
  '40_regular': {
    fontSize: pxToRem(40),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(48), // 120%
    letterSpacing: pxToRem(-0.8),
  },
  '40_medium': {
    fontSize: pxToRem(40),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(44), // 110%
    letterSpacing: pxToRem(-0.8),
  },
  '32_regular': {
    fontSize: pxToRem(32),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(36), // 110%
    letterSpacing: pxToRem(-0.64),
  },
  '32_medium': {
    fontSize: pxToRem(32),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(35.2), // 110%
    letterSpacing: pxToRem(-0.64),
  },
  '28_medium': {
    fontSize: pxToRem(28),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(30.8), // 110%
    letterSpacing: pxToRem(-0.56),
  },
  '28_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(28),
    lineHeight: pxToRem(36.4), // 130%
    letterSpacing: pxToRem(-0.56),
    textDecoration: 'underline',
  },
  '24_regular': {
    fontSize: pxToRem(24),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(31.2), // 130%
    letterSpacing: pxToRem(-0.48),
  },
  '24_medium': {
    fontSize: pxToRem(24),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(26.4), // 110%
    letterSpacing: pxToRem(-0.48),
  },
  '24_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(24),
    lineHeight: pxToRem(31.2), // 130%
    letterSpacing: pxToRem(-0.48),
    textDecoration: 'underline',
  },
  '20_regular': {
    fontSize: pxToRem(20),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(26), // 130%
    letterSpacing: pxToRem(-0.4),
  },
  '20_medium': {
    fontSize: pxToRem(20),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(24), // 120%
    letterSpacing: pxToRem(-0.4),
  },
  '20_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(20),
    lineHeight: pxToRem(26), // 130%
    letterSpacing: pxToRem(-0.4),
    textDecoration: 'underline',
  },
  '18_regular': {
    fontSize: pxToRem(18),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(25.2), // 140%
    letterSpacing: pxToRem(-0.36),
  },
  '18_medium': {
    fontSize: pxToRem(18),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(21.6), // 120%
    letterSpacing: pxToRem(-0.36),
  },
  '18_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(18),
    lineHeight: pxToRem(23.4), // 130%
    letterSpacing: pxToRem(-0.36),
    textDecoration: 'underline',
  },
  '16_regular': {
    fontSize: pxToRem(16),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(22.4), // 140%
    letterSpacing: pxToRem(-0.32),
  },
  '16_medium': {
    fontSize: pxToRem(16),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(19.2), // 120%
    letterSpacing: pxToRem(-0.32),
  },
  '16_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(20.8), // 130%
    letterSpacing: pxToRem(-0.32),
    textDecoration: 'underline',
  },
  '14_regular': {
    fontSize: pxToRem(14),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(19.6), // 140%
    letterSpacing: pxToRem(-0.28),
  },
  '14_medium': {
    fontSize: pxToRem(14),
    fontWeight: fontWeights.medium,
    lineHeight: pxToRem(16.8), // 120%
    letterSpacing: pxToRem(-0.28),
  },
  '14_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(14),
    lineHeight: pxToRem(18.2), // 130%
    letterSpacing: pxToRem(-0.28),
    textDecoration: 'underline',
  },
  '12_regular': {
    fontSize: pxToRem(12),
    fontWeight: fontWeights.regular,
    lineHeight: pxToRem(16.8), // 140%
    letterSpacing: pxToRem(0),
  },
  '12_medium-hyperlink': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(12),
    lineHeight: pxToRem(14.4), // 120%
    letterSpacing: pxToRem(-0.24),
    textDecoration: 'underline',
  },
  '12_semibold': {
    fontWeight: fontWeights.semibold,
    fontSize: pxToRem(12),
    lineHeight: pxToRem(16.8), // 140%
    letterSpacing: pxToRem(0.56),
    textTransform: 'uppercase' as TextTransformProperty,
  },
  '12_chip': {
    fontWeight: fontWeights.medium,
    fontSize: pxToRem(12),
    letterSpacing: pxToRem(-0.24),
    lineHeight: pxToRem(14.4),
  },
  '10_semibold': {
    fontWeight: fontWeights.semibold,
    fontSize: pxToRem(10),
    lineHeight: pxToRem(12),
    letterSpacing: pxToRem(0.4),
  },
};

const breakpoints = {
  values: {
    xs: ViewportSize.xs,
    sm: ViewportSize.sm,
    md: ViewportSize.md,
    lg: ViewportSize.lg,
    xl: ViewportSize.xl,
  },
};

// Returns a css expression that yields min for small and medium screens and max for large+ screens
// The center clamp value, ((100vw - 1339px) * max), will be negative or zero below the large breakpoint and >= max above the breakpoint
function getSpacingString(min: number, max: number) {
  return `clamp(${pxToRem(min)}, calc((100vw - ${
    ViewportSize.lg - 1
  }px) * ${max}), ${pxToRem(max)})`;
}

// Spacing values -1 -> 5 are responsive and match designs
// All other spacing values will default to 8px times factor
function getSpacing(factor: number) {
  switch (factor) {
    case -1:
      return pxToRem(8);
    case 0:
      return getSpacingString(12, 16);
    case 1:
      return getSpacingString(16, 24);
    case 2:
      return getSpacingString(24, 40);
    case 3:
      return getSpacingString(40, 64);
    case 4:
      return getSpacingString(48, 80);
    case 5:
      return getSpacingString(64, 120);
    default:
      return factor * 8;
  }
}

export const HEADER_HEIGHT_XS_SM = 124;
export const HEADER_HEIGHT_MD_LG_XL = 139;
export const SunrunThemeOptions: AugmentedThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#1f2647',
      light: '#cbd5de',
      dark: '#011f3a',
    },
    secondary: {
      main: '#fff6a1',
      light: '#fffdea',
      dark: '#a39d63',
    },
    error: {
      main: '#9E1E19',
      light: '#ebd1d0',
      dark: '#661411',
    },
    success: {
      main: '#007505',
      light: '#cae2cb',
      dark: '#014b04',
    },
    warning: {
      main: '#e5c100',
      light: '#faf2c9',
      dark: '#957e01',
    },
    info: {
      main: '#0065C1',
      light: '#d2e4f4',
      dark: '#01407a',
    },
  },
  typography: {
    fontFamily: 'Roobert, sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@font-face': [roobertFont],
        },
      },
    },
  },
  fontWeights,
  brandColors,
  brandType: {
    // no match, would be 120_regular
    'display-m': {
      fontWeight: 400,
      fontSize: pxToRem(120),
      letterSpacing: '-0.02rem',
      lineHeight: pxToRem(120),
    },
    // no match, would be 36_medium
    'heading-xxs': {
      fontWeight: 500,
      fontSize: pxToRem(36),
      letterSpacing: '-0.02rem',
      lineHeight: pxToRem(36),
    },
    // no match would be 12_medium
    'body-s': {
      fontWeight: 500,
      fontSize: pxToRem(12),
      letterSpacing: '-0.02rem',
      lineHeight: pxToRem(12),
    },
    // no match, would be 10_medium
    'body-xs': {
      fontWeight: 500,
      fontSize: pxToRem(10),
      letterSpacing: pxToRem(-0.2),
      lineHeight: pxToRem(16),
    },
  },
  breakpoints,
  headerHeight: {
    height: '100px',
  },
  type,
  spacing: getSpacing,
  zLevels: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    tooltip: 1040,
    modalBackdrop: 1050,
    modal: 1060,
    popover: 1070,
  },
  aspectRatio,
};

export const SunrunTheme = createTheme(SunrunThemeOptions);
